import React, { useEffect, useState } from "react";
import { Button, TextField, Tooltip } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import moment from "moment";
import useAxios from "../../helpers/Hooks/useAxios";
import { URLS } from "../../helpers/URLS";
import { toast } from "react-toastify";
import NoData from "../../loaders/NoData";
import SkeletonLoader from "../../loaders/Skeleton";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import TimelapseOutlinedIcon from "@mui/icons-material/TimelapseOutlined";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ApplyModal from "./ApplyModal";
import AttendanceCalendar, { applyBlockedBecause } from "./AttendanceCalendar";
import AttendanceLog from "./AttendanceLog";
import LocationPin from "./LocationPin";
import { useAttendanceTracker } from "../../helpers/useAttendanceTracker";
import { holidayKeys, hm, timeOf, apiDate, defaultRange } from "./helpers";
import {
    getBrowserLocation, locationBody, locationProblem,
    formatLocation, mapLink, hasCoords,
} from "../../helpers/geolocation";
import "./Attendance.scss";

/* These three figures are easy to read as something they are not — Worked is
   presence, Break is what you declared, Idle is only what this tab could
   measure — so each label carries its own definition. */
const HINTS = {
    worked: "Time present today: check-out minus check-in, less any break you declared. "
        + "It counts up while you are checked in. It is not based on activity, so time in a "
        + "meeting, on a call or away from the laptop still counts.",
    break: "The break you entered when checking out. This is the only thing deducted from Worked.",
    idle: "Time this app measured as idle: the tab hidden, the screen locked, or no typing or "
        + "clicking for five minutes. Shown for information only — it is never deducted from "
        + "Worked, and it cannot see time with the app closed.",
};

const Hint = ({ text }) => (
    <Tooltip title={text} placement="top" arrow classes={{ tooltip: "wh-tip att-hint-tip" }}>
        <InfoOutlinedIcon className="att-hint" />
    </Tooltip>
);

const MyAttendance = () => {

    const { fetchData } = useAxios();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    /* the current month to begin with, then whatever the pickers say */
    const [range, setRange] = useState(defaultRange);
    const [busy, setBusy] = useState(false);

    /* the clock runs while checked in and not yet checked out */
    const today = data?.today;
    const tracking = Boolean(today?.checkIn) && !today?.checkOut;

    const session = useAttendanceTracker({ enabled: tracking, fetchData });

    /* what the pickers hold before Show is pressed, so a half-changed range
       does not send a request on every click of the calendar */
    const [draft, setDraft] = useState(range);

    /* null when closed, otherwise 'leave', 'wfh', or 'any' from the calendar */
    const [applyMode, setApplyMode] = useState(null);

    /* the day a calendar click is asking about, so the popup opens on it */
    const [applyDate, setApplyDate] = useState(null);

    /* Clicking a day opens the popup on that date with every type offered.
       A day that already carries a request does not open one — it says what
       is already there instead, which is more use than a form that would be
       refused. */
    const pickDay = (row) => {
        const blocked = applyBlockedBecause(row);
        if (blocked) { toast.info(blocked); return; }
        setApplyDate(row.date);
        setApplyMode("any");
    };

    const closeApply = () => { setApplyMode(null); setApplyDate(null); };

    /* `quiet` refreshes in place. A punch only changes today's row, and
       swapping the whole page for a skeleton would unmount the very buttons
       that were just pressed — so that reload keeps the page on screen. */
    const load = async (period = range, { quiet = false } = {}) => {
        if (!quiet) setLoading(true);
        const resp = await fetchData({
            url: `${URLS.myAttendance}?from=${apiDate(period.from)}&to=${apiDate(period.to)}`
                + `&holidays=${encodeURIComponent(holidayKeys())}`,
            method: "get",
        });
        if (!quiet) setLoading(false);
        if (resp?.success === false) {
            toast.error(resp?.message || "Could not load your attendance");
            return;
        }
        setData(resp.data);
    };

    useEffect(() => {
        load(range);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range.from, range.to]);

    const punch = async (url, label) => {
        if (busy) return;
        setBusy(true);

        try {
            /* asked for before the punch so it can be saved with it; a refusal
               is reported but never stops someone clocking in or out */
            const place = await getBrowserLocation();

            const resp = await fetchData({ url, method: "post", body: locationBody(place) });

            if (resp?.success === false) {
                toast.error(resp?.message || `Could not ${label}`);
                return;
            }

            toast.success(resp?.message || "Done");
            if (!place.ok) toast.info(`${locationProblem(place)} The punch was saved without it.`);

            /* Awaited, and still busy while it runs: releasing the buttons
               before the new state arrives left them briefly reading the old
               one, so a second click could fire a punch the server then had
               to refuse. */
            await load(range, { quiet: true });
        } finally {
            setBusy(false);
        }
    };

    if (loading) return <SkeletonLoader />;
    if (!data) return <NoData title="No attendance yet" message="Nothing has been recorded for this period." />;

    const { summary, rows } = data;

    /* Worked is the punches, never the tracker: the span since check-in —
       up to now while the day is still open — less the declared break. The
       tracker re-renders this every few seconds while checked in, so the
       figure moves as you watch it without being derived from activity. */
    const workedToday = today?.checkIn
        ? Math.max(moment(today.checkOut || undefined).diff(moment(today.checkIn), "minutes")
            - (today.breakMinutes || 0), 0)
        : 0;

    /* The two punch buttons, worked out once. Exactly one of them is live at
       any moment: nothing punched → check in; checked in → check out; checked
       out → neither, the day is closed. A disabled button that does not say
       why reads as a broken one, so each carries its reason. */
    const punchIn = {
        done: Boolean(today?.checkIn),
        why: busy ? "Saving…"
            : today?.checkIn
                ? `Already checked in at ${timeOf(today.checkIn)} — one check-in a day.`
                : "Start your day.",
    };

    const punchOut = {
        done: Boolean(today?.checkOut),
        why: busy ? "Saving…"
            : today?.checkOut ? `Already checked out at ${timeOf(today.checkOut)}.`
                : !today?.checkIn ? "Check in first."
                    : "Close the day. Your hours are counted from your check-in.",
    };

    /* Time at the app, as the server has it plus what this tab has counted
       since the last heartbeat. Reported on its own and never deducted from
       the hours above — away from the keyboard is not away from work. */
    const liveIdle = (today?.idleMinutes || 0) + Math.floor(session.idleSeconds / 60);

    return (
        <div className="att-page">

            {/* ---------- the day in progress ---------- */}
            <div className="att-today">
                <div className="att-clock">
                    <span className="att-clock-label">
                        Today
                        {tracking && (
                            <span className={`att-live${session.idle ? " is-idle" : ""}`}>
                                <span className="att-live-dot" />
                                {session.idle ? "On a break" : "Working"}
                            </span>
                        )}
                    </span>
                    <b>{moment().format("ddd, DD MMM YYYY")}</b>
                    <span className="att-clock-times">
                        <span>
                            <em>In</em> {timeOf(today?.checkIn)}
                            <LocationPin location={today?.checkInLocation} label="Checked in" />
                        </span>
                        <span>
                            <em>Out</em> {timeOf(today?.checkOut)}
                            <LocationPin location={today?.checkOutLocation} label="Checked out" />
                        </span>
                        <span>
                            <em>Worked <Hint text={HINTS.worked} /></em>
                            {hm(workedToday)}
                        </span>
                        {today?.breakMinutes > 0 && (
                            <span>
                                <em>Break <Hint text={HINTS.break} /></em>
                                {hm(today.breakMinutes)}
                            </span>
                        )}
                        {/* what the tracker saw, labelled as what it is */}
                        <span className="att-clock-break">
                            <PauseCircleOutlineRoundedIcon />
                            <em>Idle <Hint text={HINTS.idle} /></em>
                            {hm(liveIdle)}
                        </span>
                    </span>

                    {(hasCoords(today?.checkInLocation) || hasCoords(today?.leaveLocation)) && (
                        <span className="att-clock-place">
                            <PlaceOutlinedIcon />
                            {hasCoords(today?.checkInLocation)
                                ? <>Checked in from <a href={mapLink(today.checkInLocation)}
                                    target="_blank" rel="noopener noreferrer">
                                    {formatLocation(today.checkInLocation)}</a></>
                                : <>Working from <a href={mapLink(today.leaveLocation)}
                                    target="_blank" rel="noopener noreferrer">
                                    {formatLocation(today.leaveLocation)}</a></>}
                        </span>
                    )}

                    {tracking && (
                        <span className="att-clock-note">
                            Counted while WorkHub is open. Five minutes without activity,
                            a locked screen or another tab all count as a break.
                        </span>
                    )}
                </div>

                <div className="att-today-actions">
                    <Button variant="outlined" startIcon={<EventBusyOutlinedIcon />}
                        onClick={() => setApplyMode("leave")}>
                        Apply Leave
                    </Button>
                    <Button variant="outlined" startIcon={<HomeWorkOutlinedIcon />}
                        onClick={() => setApplyMode("wfh")}>
                        Work From Home
                    </Button>

                    <span className="att-action-split" />

                    {/* MUI buttons stop firing hover events once disabled, so the
                        tooltip hangs off a wrapping span rather than the button. */}
                    <Tooltip title={punchIn.why} placement="top" arrow>
                        <span className="att-punch">
                            <Button variant="outlined" startIcon={<LoginRoundedIcon />}
                                disabled={busy || punchIn.done}
                                onClick={() => punch(URLS.attendanceCheckIn, "check in")}>
                                {punchIn.done ? "Checked in" : "Check in"}
                            </Button>
                        </span>
                    </Tooltip>

                    <Tooltip title={punchOut.why} placement="top" arrow>
                        <span className="att-punch">
                            <Button variant="contained" startIcon={<LogoutRoundedIcon />}
                                disabled={busy || !punchIn.done || punchOut.done}
                                onClick={() => punch(URLS.attendanceCheckOut, "check out")}>
                                {punchOut.done ? "Checked out" : "Check out"}
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            </div>

            {/* ---------- the period at a glance ---------- */}
            <div className="att-stats">
                <div className="att-stat">
                    <span className="att-stat-icon"><AccessTimeRoundedIcon /></span>
                    <div>
                        <span className="att-stat-label">Avg hours / day</span>
                        <b>{hm(summary.avgMinutesPerDay)}</b>
                        <em>over {summary.presentDays + summary.partialDays} worked days</em>
                    </div>
                </div>

                <div className="att-stat">
                    <span className="att-stat-icon"><EventAvailableOutlinedIcon /></span>
                    <div>
                        <span className="att-stat-label">Present</span>
                        <b>{summary.presentDays + summary.partialDays}</b>
                        <em>of {summary.workingDays} working days</em>
                    </div>
                </div>

                <div className="att-stat">
                    <span className="att-stat-icon"><EventBusyOutlinedIcon /></span>
                    <div>
                        <span className="att-stat-label">Absent</span>
                        <b>{summary.absentDays}</b>
                        <em>{summary.leaveDays} on leave</em>
                    </div>
                </div>

                <div className="att-stat">
                    <span className="att-stat-icon"><TimelapseOutlinedIcon /></span>
                    <div>
                        <span className="att-stat-label">On-time arrival</span>
                        <b>{summary.onTimePercent === null ? "--" : `${summary.onTimePercent}%`}</b>
                        <em>{summary.onTimePercent === null
                            ? "needs check-in times"
                            : `before ${summary.shift.startHour}:15`}</em>
                    </div>
                </div>
            </div>

            {/* ---------- the calendar ---------- */}
            <div className="wh-toolbar att-log-head">
                <h6 className="wh-section-title" style={{ margin: 0 }}>
                    <CalendarMonthOutlinedIcon />Attendance Calendar
                </h6>
            </div>

            <div className="wh-filter-bar att-range-bar">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                        label="From"
                        inputFormat="DD/MM/YYYY"
                        value={draft.from}
                        maxDate={draft.to}
                        onChange={(date) => {
                            const value = date && date.toDate ? date.toDate() : date;
                            setDraft((prev) => ({ ...prev, from: value }));
                        }}
                        renderInput={(params) => <TextField {...params} size="small" />}
                    />
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DesktopDatePicker
                        label="To"
                        inputFormat="DD/MM/YYYY"
                        value={draft.to}
                        minDate={draft.from}
                        onChange={(date) => {
                            const value = date && date.toDate ? date.toDate() : date;
                            setDraft((prev) => ({ ...prev, to: value }));
                        }}
                        renderInput={(params) => <TextField {...params} size="small" />}
                    />
                </LocalizationProvider>

                <Button variant="contained" startIcon={<SearchRoundedIcon />}
                    onClick={() => setRange({ ...draft })}>Show</Button>

                <Button variant="outlined" className="wh-filter-bar-end"
                    onClick={() => { const next = defaultRange(); setDraft(next); setRange(next); }}>
                    This month
                </Button>
            </div>

            <AttendanceCalendar rows={rows} onPickDay={pickDay} />

            {/* ---------- the log ---------- */}
            <div className="wh-toolbar att-log-head">
                <h6 className="wh-section-title" style={{ margin: 0 }}>Attendance Log</h6>
            </div>

            <AttendanceLog rows={rows} shift={summary.shift} />

            <ApplyModal
                show={Boolean(applyMode)}
                mode={applyMode || "leave"}
                date={applyDate}
                onClose={closeApply}
                /* quiet, so the calendar and the new request appear without
                   the page blanking to a skeleton underneath the popup */
                onSaved={() => load(range, { quiet: true })}
            />
        </div>
    );
};

export default MyAttendance;
